import { useState, useMemo, useCallback } from 'react';
import { ShoppingCart, Search, Filter, Package, Users, Store } from 'lucide-react';
import { format, parseISO } from 'date-fns';

import { CopyableField } from '../components/CopyableField';
import { StatusBadge } from '../components/StatusBadge';
import { VendorGroup } from '../components/VendorGroup';
import { useToast } from '../components/Toast';
import {
  useProcurementItems,
  useClaimProcurement,
  useReleaseProcurement,
  useMarkOrdered,
} from '../hooks/useProcurement';
import type { ProcurementListItem } from '../hooks/useProcurement';

// --- Helpers ---

function getPriorityColor(item: ProcurementListItem): string {
  // Items without a need-by date are low priority
  if (!item.needByDate) return 'bg-foreground-subtle';

  const now = new Date();
  const needBy = parseISO(item.needByDate);
  const daysUntilDue = Math.ceil(
    (needBy.getTime() - now.getTime()) / (1000 * 60 * 60 * 24)
  );

  if (daysUntilDue <= 3) return 'bg-danger';
  if (daysUntilDue <= 7) return 'bg-warning';
  if (daysUntilDue <= 14) return 'bg-primary';
  return 'bg-foreground-subtle';
}

interface VendorBucket {
  vendorName: string;
  items: ProcurementListItem[];
  earliestNeedBy: string | null;
}

function groupByVendor(items: ProcurementListItem[]): VendorBucket[] {
  const map = new Map<string, ProcurementListItem[]>();

  for (const item of items) {
    const vendor = item.vendorName ?? 'Unknown Vendor';
    const existing = map.get(vendor);
    if (existing) {
      existing.push(item);
    } else {
      map.set(vendor, [item]);
    }
  }

  const buckets: VendorBucket[] = [];
  for (const [vendorName, vendorItems] of map) {
    // Sort within vendor: needByDate ASC, then partNumber alphabetical
    vendorItems.sort((a, b) => {
      if (a.needByDate && b.needByDate) {
        const cmp = a.needByDate.localeCompare(b.needByDate);
        if (cmp !== 0) return cmp;
      }
      if (a.needByDate && !b.needByDate) return -1;
      if (!a.needByDate && b.needByDate) return 1;
      return a.partNumber.localeCompare(b.partNumber);
    });

    const earliestNeedBy = vendorItems.reduce<string | null>((earliest, item) => {
      if (!item.needByDate) return earliest;
      if (!earliest) return item.needByDate;
      return item.needByDate < earliest ? item.needByDate : earliest;
    }, null);

    buckets.push({ vendorName, items: vendorItems, earliestNeedBy });
  }

  // Sort vendor groups by earliest need-by date
  buckets.sort((a, b) => {
    if (a.earliestNeedBy && b.earliestNeedBy) {
      return a.earliestNeedBy.localeCompare(b.earliestNeedBy);
    }
    if (a.earliestNeedBy && !b.earliestNeedBy) return -1;
    if (!a.earliestNeedBy && b.earliestNeedBy) return 1;
    return a.vendorName.localeCompare(b.vendorName);
  });

  return buckets;
}

// --- Inline PO Input Component ---

function InlinePoInput({
  onSubmit,
  onCancel,
  isLoading,
}: {
  onSubmit: (poNumber: string) => void;
  onCancel: () => void;
  isLoading: boolean;
}) {
  const [value, setValue] = useState('');

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && value.trim()) {
      onSubmit(value.trim());
    } else if (e.key === 'Escape') {
      onCancel();
    }
  };

  return (
    <input
      type="text"
      value={value}
      onChange={(e) => setValue(e.target.value)}
      onKeyDown={handleKeyDown}
      onBlur={onCancel}
      placeholder="PO #, Enter to save"
      disabled={isLoading}
      autoFocus
      className="w-32 px-2 py-1 text-xs rounded border border-border bg-background text-foreground focus:outline-none focus:ring-1 focus:ring-primary disabled:opacity-50"
    />
  );
}

// --- Main Page Component ---

export function ToOrderPage() {
  const [vendorFilter, setVendorFilter] = useState<string>('');
  const [unclaimedOnly, setUnclaimedOnly] = useState(true);
  const [partSearch, setPartSearch] = useState('');
  const [orderingItemId, setOrderingItemId] = useState<string | null>(null);

  const { showToast, ToastContainer } = useToast();

  // Fetch needed items (includes released items that were unclaimed)
  const { data: response, isLoading, error } = useProcurementItems({ status: 'needed' });

  const claimMutation = useClaimProcurement();
  const releaseMutation = useReleaseProcurement();
  const markOrderedMutation = useMarkOrdered();

  // Filter and group items
  const { vendorBuckets, allItems, vendors, stats } = useMemo(() => {
    const allItems = response?.data ?? [];

    // Apply filters
    let filtered = allItems;

    if (unclaimedOnly) {
      filtered = filtered.filter((item) => !item.claimedBy);
    }

    if (vendorFilter) {
      filtered = filtered.filter(
        (item) => (item.vendorName ?? 'Unknown Vendor') === vendorFilter
      );
    }

    if (partSearch.trim()) {
      const search = partSearch.trim().toLowerCase();
      filtered = filtered.filter((item) =>
        item.partNumber.toLowerCase().includes(search)
      );
    }

    const vendorBuckets = groupByVendor(filtered);

    // Get unique vendor list from all items (before filtering)
    const vendorSet = new Set<string>();
    for (const item of allItems) {
      vendorSet.add(item.vendorName ?? 'Unknown Vendor');
    }
    const vendors = [...vendorSet].sort();

    const unclaimed = allItems.filter((item) => !item.claimedBy).length;
    const stats = {
      total: allItems.length,
      unclaimed,
      vendorCount: vendors.length,
    };

    return { vendorBuckets, allItems, vendors, stats };
  }, [response, unclaimedOnly, vendorFilter, partSearch]);

  // --- Action handlers ---

  const handleClaim = useCallback(
    (id: string) => {
      claimMutation.mutate(id, {
        onSuccess: () => showToast('Item claimed', 'success'),
        onError: (err) =>
          showToast(err instanceof Error ? err.message : 'Failed to claim', 'error'),
      });
    },
    [claimMutation, showToast]
  );

  const handleRelease = useCallback(
    (id: string) => {
      releaseMutation.mutate(id, {
        onSuccess: () => showToast('Claim released', 'success'),
        onError: (err) =>
          showToast(
            err instanceof Error ? err.message : 'Failed to release',
            'error'
          ),
      });
    },
    [releaseMutation, showToast]
  );

  const handleMarkOrdered = useCallback(
    (id: string, gpPoNumber: string) => {
      markOrderedMutation.mutate(
        { id, gpPoNumber },
        {
          onSuccess: () => {
            showToast(`Marked as ordered (PO: ${gpPoNumber})`, 'success');
            setOrderingItemId(null);
          },
          onError: (err) =>
            showToast(
              err instanceof Error ? err.message : 'Failed to mark ordered',
              'error'
            ),
        }
      );
    },
    [markOrderedMutation, showToast]
  );

  // --- Loading / Error states ---

  if (isLoading) {
    return (
      <div className="space-y-4">
        <div className="h-8 w-48 bg-background-muted rounded animate-pulse" />
        <div className="h-4 w-72 bg-background-muted rounded animate-pulse" />
        <div className="grid grid-cols-3 gap-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-20 bg-background-muted rounded-lg animate-pulse" />
          ))}
        </div>
        <div className="h-48 bg-background-muted rounded-lg animate-pulse" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-4">
        <h1 className="text-2xl font-semibold text-foreground">To Order</h1>
        <div className="rounded-lg border border-danger bg-danger-subtle p-6 text-center">
          <p className="text-sm text-danger">
            Failed to load procurement items: {error instanceof Error ? error.message : 'Unknown error'}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div>
        <h1 className="text-2xl font-semibold text-foreground">To Order</h1>
        <p className="text-sm text-foreground-muted mt-1">
          Parts that need purchase orders created
        </p>
      </div>

      {/* Filter Bar */}
      <div className="flex flex-wrap items-center gap-3">
        {/* Vendor Dropdown */}
        <div className="relative">
          <Filter className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-foreground-muted" />
          <select
            value={vendorFilter}
            onChange={(e) => setVendorFilter(e.target.value)}
            className="pl-8 pr-8 py-2 text-sm rounded-md border border-border bg-background text-foreground appearance-none cursor-pointer focus:outline-none focus:ring-1 focus:ring-primary"
          >
            <option value="">All Vendors</option>
            {vendors.map((vendor) => (
              <option key={vendor} value={vendor}>
                {vendor}
              </option>
            ))}
          </select>
        </div>

        {/* Unclaimed Only Toggle */}
        <label className="inline-flex items-center gap-2 cursor-pointer select-none">
          <div className="relative">
            <input
              type="checkbox"
              checked={unclaimedOnly}
              onChange={(e) => setUnclaimedOnly(e.target.checked)}
              className="sr-only peer"
            />
            <div className="w-9 h-5 bg-background-muted rounded-full peer peer-checked:bg-primary transition-colors" />
            <div className="absolute left-0.5 top-0.5 w-4 h-4 bg-white rounded-full shadow peer-checked:translate-x-4 transition-transform" />
          </div>
          <span className="text-sm text-foreground-muted">Unclaimed only</span>
        </label>

        {/* Part Number Search */}
        <div className="relative flex-1 max-w-xs">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-foreground-muted" />
          <input
            type="text"
            value={partSearch}
            onChange={(e) => setPartSearch(e.target.value)}
            placeholder="Search part number..."
            className="w-full pl-8 pr-3 py-2 text-sm rounded-md border border-border bg-background text-foreground placeholder:text-foreground-muted focus:outline-none focus:ring-1 focus:ring-primary"
          />
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="bg-background rounded-lg border border-border p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-md bg-primary/10">
              <Package className="h-5 w-5 text-primary" />
            </div>
            <div>
              <p className="text-2xl font-semibold text-foreground">{stats.total}</p>
              <p className="text-xs text-foreground-muted">Items needing POs</p>
            </div>
          </div>
        </div>
        <div className="bg-background rounded-lg border border-border p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-md bg-warning-subtle">
              <Users className="h-5 w-5 text-warning" />
            </div>
            <div>
              <p className="text-2xl font-semibold text-foreground">{stats.unclaimed}</p>
              <p className="text-xs text-foreground-muted">Unclaimed items</p>
            </div>
          </div>
        </div>
        <div className="bg-background rounded-lg border border-border p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-md bg-success-subtle">
              <Store className="h-5 w-5 text-success" />
            </div>
            <div>
              <p className="text-2xl font-semibold text-foreground">{stats.vendorCount}</p>
              <p className="text-xs text-foreground-muted">Vendors with needs</p>
            </div>
          </div>
        </div>
      </div>

      {/* Vendor Groups */}
      {vendorBuckets.length === 0 ? (
        <div className="rounded-lg border border-dashed border-border bg-background p-12 text-center">
          <ShoppingCart className="h-10 w-10 text-foreground-muted mx-auto mb-3" />
          <p className="text-sm font-medium text-foreground-muted">
            All parts have been ordered
          </p>
          <p className="text-xs text-foreground-subtle mt-1">
            {allItems.length > 0
              ? 'No items match the current filters'
              : 'Nothing to order right now'}
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {vendorBuckets.map((bucket) => (
            <VendorGroup
              key={bucket.vendorName}
              vendorName={bucket.vendorName}
              itemCount={bucket.items.length}
              earliestNeedBy={bucket.earliestNeedBy}
            >
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-border text-left">
                      <th className="px-4 py-2 text-xs font-medium text-foreground-muted w-8" />
                      <th className="px-4 py-2 text-xs font-medium text-foreground-muted">
                        Part Number
                      </th>
                      <th className="px-4 py-2 text-xs font-medium text-foreground-muted">
                        Qty
                      </th>
                      <th className="px-4 py-2 text-xs font-medium text-foreground-muted">
                        Need By
                      </th>
                      <th className="px-4 py-2 text-xs font-medium text-foreground-muted">
                        Status
                      </th>
                      <th className="px-4 py-2 text-xs font-medium text-foreground-muted text-right">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {bucket.items.map((item) => {
                      const isUnclaimed = !item.claimedBy;
                      const isMutating =
                        claimMutation.isPending ||
                        releaseMutation.isPending ||
                        markOrderedMutation.isPending;

                      return (
                        <tr
                          key={item.id}
                          className={`border-b border-border last:border-b-0 hover:bg-background-subtle transition-colors ${
                            isUnclaimed ? 'border-l-4 border-l-warning' : ''
                          }`}
                        >
                          {/* Priority indicator */}
                          <td className="px-4 py-2.5">
                            <span
                              className={`inline-block h-2.5 w-2.5 rounded-full ${getPriorityColor(item)}`}
                              title={
                                item.needByDate
                                  ? `Need by ${format(parseISO(item.needByDate), 'MMM d, yyyy')}`
                                  : 'No due date'
                              }
                            />
                          </td>

                          {/* Part Number */}
                          <td className="px-4 py-2.5">
                            <CopyableField value={item.partNumber} mono />
                          </td>

                          {/* Quantity */}
                          <td className="px-4 py-2.5">
                            <CopyableField value={item.quantity} />
                          </td>

                          {/* Need By Date */}
                          <td className="px-4 py-2.5 text-foreground-muted">
                            {item.needByDate
                              ? format(parseISO(item.needByDate), 'MMM d, yyyy')
                              : '\u2014'}
                          </td>

                          {/* Status */}
                          <td className="px-4 py-2.5">
                            <StatusBadge status={item.status} />
                          </td>

                          {/* Actions */}
                          <td className="px-4 py-2.5 text-right">
                            <div className="inline-flex items-center gap-2">
                              {item.status === 'needed' && (
                                <button
                                  onClick={() => handleClaim(item.id)}
                                  disabled={isMutating}
                                  className="px-3 py-1 text-xs font-medium rounded-md bg-primary text-primary-foreground hover:bg-primary-hover transition-colors disabled:opacity-50"
                                >
                                  Claim
                                </button>
                              )}

                              {item.status === 'claimed' && (
                                <>
                                  {orderingItemId === item.id ? (
                                    <InlinePoInput
                                      onSubmit={(po) => handleMarkOrdered(item.id, po)}
                                      onCancel={() => setOrderingItemId(null)}
                                      isLoading={markOrderedMutation.isPending}
                                    />
                                  ) : (
                                    <button
                                      onClick={() => setOrderingItemId(item.id)}
                                      disabled={isMutating}
                                      className="px-3 py-1 text-xs font-medium rounded-md bg-primary text-primary-foreground hover:bg-primary-hover transition-colors disabled:opacity-50"
                                    >
                                      Order
                                    </button>
                                  )}
                                  <button
                                    onClick={() => handleRelease(item.id)}
                                    disabled={isMutating}
                                    className="px-3 py-1 text-xs font-medium rounded-md border border-border text-foreground-muted hover:bg-background-muted transition-colors disabled:opacity-50"
                                  >
                                    Release
                                  </button>
                                </>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </VendorGroup>
          ))}
        </div>
      )}

      <ToastContainer />
    </div>
  );
}
